import React from "react";
import { loadGeotabDay } from "./useGeotabDay.js";
import useConsolidationCapture from "./useConsolidationCapture.js";
import { fillFromGeotab, geotabTruckOf } from "../utils/consolidationGeotabFill.js";
import { orderByDeparture } from "../utils/consolidationVisitOrder.js";
import { captureGeotabDecision, finishGeotabFill, sheetCaptureBase } from "../utils/consolidationCaptures.js";

// Après confirmation, les heures Geotab des arrêts et du service remplacent celles du brouillon,
// marquées « à vérifier » jusqu'à la sauvegarde ; la saisie manuelle reste possible.
// reason : pourquoi l'action est grisée (comparatif Geotab en vérification).
export default function useConsolidationGeotabFill({ row, setRow, setDirty, onEdit, onFilled, editable, reason = "", quantity }) {
  const [state, setState] = React.useState({ busy: false, message: "", pending: null });
  const trigger = React.useRef(null);
  // Captures : le clic part à son issue ; « Ajouter » ou « Annuler » le suit comme parent.
  const capture = useConsolidationCapture();
  const decision = React.useRef(null);
  const decide = (added) => { captureGeotabDecision(capture, decision.current, row, added); decision.current = null; };
  const apply = (result) => {
    // Le chauffeur choisit sa route : les départs réels fixent l'ordre de tout le relevé.
    const next = { ...result.row, visits: orderByDeparture(result.row, quantity) };
    onEdit();
    setRow(next);
    setDirty(true);
    setState({ busy: false, pending: null, message: "" });
    requestAnimationFrame(() => onFilled?.(trigger.current));
  };
  const start = async (button) => {
    if (!editable || state.busy) return;
    trigger.current = button;
    const tracked = capture?.start("consolidation_geotab_fill", { ...sheetCaptureBase(row), title: "Ajouter les heures Geotab" });
    setState({ busy: true, pending: null, message: "" });
    try {
      const data = await loadGeotabDay(row.dispatch_date, row.dispatch_period || "day");
      const truck = geotabTruckOf(data, row);
      if (!truck?.comparison) {
        const message = truck ? "Geotab : aucun trajet pour ce camion ce jour-là." : "Geotab : camion introuvable.";
        finishGeotabFill(tracked, truck ? "no_trips" : "truck_not_found", { data, truck, message });
        setState({ busy: false, pending: null, message });
        return;
      }
      const result = fillFromGeotab(row, truck);
      if (!result.filled && !result.service) {
        const message = "Geotab : aucun arrêt ne correspond à ce relevé.";
        finishGeotabFill(tracked, "no_match", { data, truck, message });
        setState({ busy: false, pending: null, message });
      } else {
        decision.current = finishGeotabFill(tracked, "ready", { data, truck, result });
        setState({ busy: false, pending: result, message: "" });
      }
    } catch (error) {
      finishGeotabFill(tracked, "unavailable", { message: error.message || "Geotab est indisponible." });
      setState({ busy: false, pending: null, message: error.message || "Geotab est indisponible." });
    }
  };
  // Comme une saisie à la main : permis même quand une correction du Dispatch attend, qui conserve les saisies.
  return { ...state, trigger: trigger.current, editable, reason, start,
    confirm: () => { if (state.pending) { decide(true); apply(state.pending); } },
    cancel: () => { decide(false); setState((s) => ({ ...s, pending: null })); } };
}
